/**
 * RealCalendarMultiProvider
 * ──────────────────────────────────────────────────────────────────────
 * Real-mode multi-provider calendar day view. Replaces the empty-state
 * placeholder previously rendered for the Calendar tab in real mode.
 *
 * Data sources (all API-backed, no demo literals):
 *   • Providers  → schedulerApi.listEntities({ is_active: true })
 *   • Locations  → schedulerApi.listLocations()  (for optional filtering)
 *   • Appointments → schedulerApi.listAppointmentsPreferEnriched({ date })
 *
 * Layout:
 *   • Date navigation pill (‹ ›, Today, long-form date)
 *   • Optional location filter (shows when the tenant has >1 location)
 *   • Provider checkbox strip — users pick which columns to show
 *   • ProviderColumnGrid time-grid (8 AM – 6 PM by default)
 *   • Current-time indicator overlay when viewing today
 *
 * Empty & loading states are first-class so the screen never shows
 * stale data. Errors surface via a small inline alert with a retry.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { capabilitiesFor } from '../../../../lib/permissions';
import * as schedulerApi from '../../../../lib/api/schedulerApi';
import ProviderColumnGrid from './ProviderColumnGrid';
import CurrentTimeLine from './CurrentTimeLine';
import MonthUtilizationStrip from './MonthUtilizationStrip';
import OverbookCapacityStrip from './OverbookCapacityStrip';
import OverbookDialog from './OverbookDialog';
import ReleaseOverbookButton from './ReleaseOverbookButton';
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import { Card, CardContent } from '../../../ui/card';
import { cn } from '../../../../lib/utils';
import {
  ChevronLeft, ChevronRight, Calendar as CalIcon, RefreshCw,
  AlertTriangle, Loader2, Users, MapPin,
} from 'lucide-react';

const HOURS_START = 8;
const HOURS_END = 18;
const HOUR_PX = 56;

const PROVIDER_COLORS = [
  '#3b82f6', '#8b5cf6', '#22c55e', '#f59e0b',
  '#ef4444', '#06b6d4', '#ec4899', '#14b8a6',
  '#a855f7', '#f97316', '#64748b', '#eab308',
];

const isoDate = (d) => d.toISOString().slice(0, 10);
const parseIso = (s) => {
  const [y, m, day] = s.split('-').map((n) => parseInt(n, 10));
  return new Date(y, m - 1, day);
};
const fmtLong = (d) => d.toLocaleDateString('en-US', {
  weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
});

const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);
const timeFromIso = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
const durationFromAppt = (a) => {
  if (a.start && a.end) {
    const start = new Date(a.start).getTime();
    const end = new Date(a.end).getTime();
    const diff = Math.round((end - start) / 60000);
    return diff > 0 ? diff : 30;
  }
  return a.duration_min || 30;
};

const RealCalendarMultiProvider = ({ onBook }) => {
  const { user } = useAuth();
  const { canSchedulerAdmin } = capabilitiesFor(user);
  // Overbooking is admin-only on the UI — backend enforces the same
  // allowlist via policy, but hiding the CTA keeps the surface honest
  // for read_only / front_desk / provider / MA roles.
  const allowOverbook = !!canSchedulerAdmin;
  const [date, setDate] = useState(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  });
  const [entities, setEntities] = useState([]);
  const [locations, setLocations] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [selectedEntityIds, setSelectedEntityIds] = useState([]);
  const [locationFilter, setLocationFilter] = useState(null); // null = all
  const [providerLoading, setProviderLoading] = useState(true);
  const [apptLoading, setApptLoading] = useState(false);
  const [error, setError] = useState(null);
  // Overbooking state — dialog open + prefill (entity selected from
  // capacity strip), and a refreshKey to force the strip to re-fetch
  // after a successful book / release.
  const [overbookOpen, setOverbookOpen] = useState(false);
  const [overbookEntityId, setOverbookEntityId] = useState('');
  const [capacityRefreshKey, setCapacityRefreshKey] = useState(0);

  const gridBodyRef = useRef(null);

  /* ── Load providers, then locations, then appointments.
        Sequential order (not Promise.all) so the UI can show a precise
        cause when one step fails or returns empty. ── */
  const [locationLoading, setLocationLoading] = useState(true);
  const [providerEmpty, setProviderEmpty] = useState(false);
  const [locationEmpty, setLocationEmpty] = useState(false);

  const loadRefs = useCallback(async () => {
    setProviderLoading(true);
    setLocationLoading(true);
    setError(null);
    setProviderEmpty(false);
    setLocationEmpty(false);

    // 1. Providers (entities) ─ block subsequent steps if this fails.
    const eRes = await schedulerApi.listEntities({ is_active: true });
    if (eRes.ok) {
      const items = eRes.data?.entities || eRes.data?.items || [];
      const colored = items.map((e, i) => ({
        ...e,
        color: e.color || PROVIDER_COLORS[i % PROVIDER_COLORS.length],
      }));
      setEntities(colored);
      setProviderEmpty(colored.length === 0);
      // 3. Apply user-context default (if scheduler_default_entity exists),
      //    otherwise fall back to first 6 so the grid doesn't blow up.
      const userDefault = user?.scheduler_default_entity_id
                       || user?.preferences?.scheduler_default_entity_id;
      const seed = userDefault && colored.some(c => c.id === userDefault)
        ? [userDefault]
        : colored.slice(0, 6).map(c => c.id);
      setSelectedEntityIds(seed);
    } else {
      setError(eRes.error || { message: 'Failed to load providers' });
      setProviderLoading(false);
      setLocationLoading(false);
      return;
    }
    setProviderLoading(false);

    // 2. Locations.
    const lRes = await schedulerApi.listLocations().catch(() => ({ ok: false }));
    if (lRes.ok) {
      const locs = lRes.data?.locations || lRes.data?.items || [];
      setLocations(locs);
      setLocationEmpty(locs.length === 0);
      const userDefaultLoc = user?.scheduler_default_location_id
                          || user?.preferences?.scheduler_default_location_id;
      if (userDefaultLoc && locs.some(l => l.id === userDefaultLoc)) {
        setLocationFilter(userDefaultLoc);
      }
    } else {
      // Locations failing is non-fatal — schedule can still render.
      setLocationEmpty(true);
    }
    setLocationLoading(false);
  }, [user]);

  useEffect(() => { loadRefs(); }, [loadRefs]);

  /* ── Load appointments whenever date or selection changes ── */
  const fetchAppts = useCallback(async () => {
    if (selectedEntityIds.length === 0) {
      setAppointments([]);
      return;
    }
    setApptLoading(true);
    // Prefer enriched payload when provisioned; transparently falls back
    // to /appointments on 404/501. Both shapes are normalized identically
    // by `lib/api/schedulerAdapter.normalizeAppointment` downstream.
    const res = await schedulerApi.listAppointmentsPreferEnriched({ date: isoDate(date) });
    if (res.ok) {
      const raw = res.data?.appointments || res.data?.items || [];
      setAppointments(raw);
      setError(null);
    } else {
      setError(res.error || { message: 'Failed to load appointments' });
    }
    setApptLoading(false);
  }, [date, selectedEntityIds.length]);

  useEffect(() => { fetchAppts(); }, [fetchAppts]);

  const visibleEntities = useMemo(() => {
    const all = entities.filter((e) => !locationFilter || e.location_id === locationFilter);
    return all.filter((e) => selectedEntityIds.includes(e.id));
  }, [entities, selectedEntityIds, locationFilter]);

  /* ── Map API appointment shape to ProviderColumnGrid shape ── */
  const gridAppointments = useMemo(() => appointments.map((a) => ({
    id: a.id,
    provider: a.entity_id,
    time: timeFromIso(a.start),
    durationMin: durationFromAppt(a),
    patient: a.patient_name || '',
    type: a.visit_type_name || '',
    status: a.status || 'scheduled',
    isOverbooked: !!a.is_overbooked,
  })), [appointments]);

  const providersForGrid = useMemo(() => visibleEntities.map((e) => ({
    id: e.id,
    name: e.name || 'Provider',
    color: e.color,
    location: e.location_name || '',
    todayCount: gridAppointments.filter((a) => a.provider === e.id).length,
  })), [visibleEntities, gridAppointments]);

  /* ── Date nav helpers ── */
  const shiftDate = (delta) => {
    const next = new Date(date);
    next.setDate(next.getDate() + delta);
    setDate(next);
  };
  const goToday = () => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    setDate(t);
  };
  const isToday = isoDate(date) === isoDate(new Date());

  /* ── Provider toggle ── */
  const toggleProvider = (id) => {
    setSelectedEntityIds((prev) => (
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    ));
  };

  /* ── Appointment block renderer ── */
  const renderBlock = (apt) => (
    <div
      className={cn(
        'h-full rounded-md border px-1.5 py-1 overflow-hidden cursor-default relative',
        apt.isOverbooked ? 'bg-amber-50 border-amber-300' :
          apt.status === 'confirmed' ? 'bg-emerald-50 border-emerald-200' :
            apt.status === 'cancelled' ? 'bg-slate-100 border-slate-200 opacity-60' :
              apt.status === 'flagged' ? 'bg-red-50 border-red-200' :
                'bg-slate-50 border-slate-200'
      )}
      title={`${apt.patient} · ${apt.type}${apt.isOverbooked ? ' · OVERBOOKED' : ''}`}
      data-testid={apt.isOverbooked ? `appt-block-overbooked-${apt.id}` : undefined}
    >
      <div className="text-[10px] font-semibold text-slate-800 truncate">{apt.patient || '—'}</div>
      <div className="text-[9px] text-slate-500 truncate">{apt.type}</div>
      {apt.isOverbooked && allowOverbook && (
        <div className="absolute top-0.5 right-0.5">
          <ReleaseOverbookButton
            bookingId={apt.id}
            cancel={true}
            onReleased={() => {
              fetchAppts();
              setCapacityRefreshKey((k) => k + 1);
            }}
            testId={`release-overbook-${apt.id}`}
          />
        </div>
      )}
    </div>
  );

  const renderOpenSlot = () => (
    <div className="h-full rounded-md border border-dashed border-slate-200 bg-transparent" />
  );

  /* ── Early returns for loading / error ── */
  if (providerLoading) {
    return (
      <Card className="border-slate-200" data-testid="real-cal-loading">
        <CardContent className="p-8 text-center">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto mb-2" />
          <p className="text-[11px] text-slate-500">Loading providers…</p>
        </CardContent>
      </Card>
    );
  }

  if (entities.length === 0) {
    return (
      <Card className="border-dashed border-slate-200" data-testid="real-cal-empty">
        <CardContent className="p-10 text-center">
          <Users className="h-7 w-7 text-slate-300 mx-auto mb-2" />
          <p className="text-sm font-medium text-slate-600">No providers configured</p>
          <p className="text-[11px] text-slate-400 mt-1">Add providers in Settings → Providers to populate the calendar.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="real-multi-provider-calendar">
      {/* Month overview stats — vendor §M9c /slots/calendar. Soft-fails
          on partial data; never blocks the day grid below. */}
      <MonthUtilizationStrip locationId={locationFilter} anchorDate={date} />
      {/* ── Toolbar: date nav + location filter ── */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1">
          <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => shiftDate(-1)} data-testid="real-cal-prev">
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm"
            variant={isToday ? 'default' : 'outline'}
            className={cn('h-8 text-[11px] px-2', isToday && 'bg-[#111] hover:bg-[#222]')}
            onClick={goToday}
            data-testid="real-cal-today"
          >
            Today
          </Button>
          <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => shiftDate(1)} data-testid="real-cal-next">
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
          <div className="flex items-center gap-1.5 ml-2 text-[12px] font-semibold text-slate-800">
            <CalIcon className="h-3.5 w-3.5 text-slate-500" />
            <span data-testid="real-cal-date-label">{fmtLong(date)}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {locations.length > 1 && (
            <div className="flex items-center gap-1 flex-wrap" data-testid="real-cal-loc-filter">
              <button
                onClick={() => setLocationFilter(null)}
                className={cn('text-[10px] px-2 py-1 rounded-full border', !locationFilter ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 text-slate-600 bg-white hover:bg-slate-50')}
                style={{ cursor: 'pointer' }}
                data-testid="real-cal-loc-all"
              >
                All locations
              </button>
              {locations.map((l) => (
                <button
                  key={l.id}
                  onClick={() => setLocationFilter(l.id)}
                  className={cn('text-[10px] px-2 py-1 rounded-full border inline-flex items-center gap-1', locationFilter === l.id ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 text-slate-600 bg-white hover:bg-slate-50')}
                  style={{ cursor: 'pointer' }}
                  data-testid={`real-cal-loc-${l.id}`}
                >
                  <MapPin className="h-2.5 w-2.5" /> {l.name}
                </button>
              ))}
            </div>
          )}
          <Button size="sm" variant="outline" className="h-8 text-[11px] gap-1" onClick={fetchAppts} disabled={apptLoading} data-testid="real-cal-refresh">
            <RefreshCw className={cn('h-3 w-3', apptLoading && 'animate-spin')} /> Refresh
          </Button>
        </div>
      </div>

      {/* ── Provider checkbox strip ── */}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="real-cal-provider-strip">
        {entities
          .filter((e) => !locationFilter || e.location_id === locationFilter)
          .map((e) => {
            const on = selectedEntityIds.includes(e.id);
            return (
              <button
                key={e.id}
                onClick={() => toggleProvider(e.id)}
                className={cn(
                  'text-[10px] px-2 py-1 rounded-full border inline-flex items-center gap-1.5 transition-colors',
                  on ? 'bg-white border-slate-300 text-slate-800' : 'bg-slate-50 border-slate-200 text-slate-400',
                )}
                style={{ cursor: 'pointer' }}
                data-testid={`real-cal-provider-toggle-${e.id}`}
                aria-pressed={on}
              >
                <span className="h-2 w-2 rounded-full" style={{ background: e.color || '#94a3b8', opacity: on ? 1 : 0.4 }} />
                {e.name}
              </button>
            );
          })}
      </div>

      {/* ── Reference-data state banners (sequential load) ── */}
      {!providerLoading && providerEmpty && (
        <Card className="border-dashed border-slate-200" data-testid="real-cal-providers-empty">
          <CardContent className="p-4 text-center">
            <AlertTriangle className="h-4 w-4 text-slate-400 mx-auto mb-1" />
            <p className="text-[11px] font-medium text-slate-600">No providers configured for this tenant</p>
            <p className="text-[10px] text-slate-400 mt-0.5">Add a provider in Scheduler Settings → Providers to populate the calendar.</p>
          </CardContent>
        </Card>
      )}
      {!locationLoading && locationEmpty && !providerEmpty && (
        <div
          className="flex items-center gap-2 text-[10px] text-slate-500"
          data-testid="real-cal-locations-empty"
        >
          <MapPin className="h-3 w-3 text-slate-400" />
          No locations configured — showing all providers.
        </div>
      )}
      {error && (
        <Card className="border-red-200 bg-red-50" data-testid="real-cal-refs-error">
          <CardContent className="p-3 flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5 text-red-500 shrink-0" />
            <div className="flex-1">
              <p className="text-[11px] font-semibold text-red-700">Couldn't load scheduler reference data</p>
              <p className="text-[10px] text-red-600">{error.message || 'Server error'}</p>
            </div>
            <Button size="sm" variant="outline" className="text-[10px] h-7 border-red-300 text-red-700" onClick={loadRefs} data-testid="real-cal-refs-retry">
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ── Overbooking capacity strip (hides itself when nothing notable) ── */}
      <OverbookCapacityStrip
        date={isoDate(date)}
        entityIds={selectedEntityIds}
        locationId={locationFilter}
        refreshKey={capacityRefreshKey}
        allowOverbook={allowOverbook}
        onOverbookClick={(eid) => {
          if (!allowOverbook) return;
          setOverbookEntityId(eid);
          setOverbookOpen(true);
        }}
      />

      {/* ── Error banner ── */}
      {error && (
        <div className="flex items-center gap-2 p-2 rounded-md bg-red-50 border border-red-100" data-testid="real-cal-error">
          <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
          <span className="text-[11px] text-red-700 flex-1">{error.message || 'Failed to load'}</span>
          <button onClick={fetchAppts} className="text-[11px] text-red-700 underline" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>Retry</button>
        </div>
      )}

      {/* ── Grid ── */}
      {visibleEntities.length === 0 ? (
        <Card className="border-dashed border-slate-200" data-testid="real-cal-no-selection">
          <CardContent className="p-8 text-center">
            <p className="text-[11px] text-slate-500">Select at least one provider to view the schedule.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="relative" ref={gridBodyRef}>
          {apptLoading && (
            <div className="absolute top-2 right-2 z-20 inline-flex items-center gap-1 text-[10px] text-slate-500 bg-white/80 backdrop-blur px-2 py-1 rounded" data-testid="real-cal-appts-loading">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading…
            </div>
          )}
          <ProviderColumnGrid
            providers={providersForGrid}
            appointments={gridAppointments}
            hoursStart={HOURS_START}
            hoursEnd={HOURS_END}
            hourPx={HOUR_PX}
            renderBlock={renderBlock}
            renderOpenSlot={renderOpenSlot}
            timeLine={isToday ? <CurrentTimeLine dayStartHour={HOURS_START} dayEndHour={HOURS_END} hourPx={HOUR_PX} gutterWidth={60} /> : null}
            onAddSlot={(providerId, hhmm) => onBook && onBook({ time: hhmm, providerId, date: isoDate(date) })}
            testId="real-provider-grid"
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-[10px] text-slate-400" data-testid="real-cal-appt-count">
              {gridAppointments.length} appointment{gridAppointments.length === 1 ? '' : 's'} · {visibleEntities.length} provider{visibleEntities.length === 1 ? '' : 's'}
            </span>
            <Badge variant="outline" className="text-[9px] border-slate-200 text-slate-500">Real mode · live data</Badge>
          </div>
        </div>
      )}

      {/* ── Overbooking confirm dialog (A24/A25) ── */}
      <OverbookDialog
        open={overbookOpen}
        onOpenChange={setOverbookOpen}
        prefillEntityId={overbookEntityId}
        prefillStart={`${isoDate(date)}T09:00:00`}
        prefillLocationId={locationFilter || ''}
        onBooked={() => {
          fetchAppts();
          setCapacityRefreshKey((k) => k + 1);
        }}
      />
    </div>
  );
};

export default RealCalendarMultiProvider;
